import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import {
  cloneDeep,
  first as firstL,
  get,
  merge,
  isEmpty,
  isNumber,
  unset,
  extend,
  forEach,
  isEqual,
  isNil,
  range
} from 'lodash';

import { AGENT_MODEL, ApiService, Device, DialogService, IDeviceData } from '@nrwl/common-lib';
import { outputParams, setupConfig, stateConfig } from './types';
import { BaseSetupTab } from './base.setup.tab';
import { TestStatusDialogComponent } from './test-status-dialog/test-status-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IEngineInfo } from './components/model/model';

@Injectable()
export class BaseSetupService {
  defaultId: string;
  id: string;
  defaultParamsById: outputParams;

  options: {
    visible: boolean;
  };
  setupActive: boolean;
  output: outputParams;
  tabs: any; //eslint-disable-line
  currentTab: BaseSetupTab;
  isLoading: boolean;
  reOpenConfirmation: boolean;
  dirtyFlag: boolean;
  dialogServiceMsgMap: outputParams;
  method: string;
  errors: Array<string>;
  params: any; //eslint-disable-line

  // eslint-disable-next-line @typescript-eslint/ban-types
  reOpen: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onSuccess: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onFail: Function;
  // eslint-disable-next-line
  tabClass: any;

  // properties initialized from wizardConfig.valuesFixed
  dataUrl: string;
  currentConfig: stateConfig;
  formatData: outputParams; // eg {alias: "", service: {…}, originator: "", notifications: "", meta_data: "", …}
  noResponseError: boolean;
  title: string;
  description: string;
  testType: string;
  type: string;
  iconName: string;
  iconType: string;
  mode = 'new';
  editMode = false;
  dataSaved = false;
  requestFailed = false;
  errorMessage: string;
  errorDescription: string;
  successMessage: string;
  apiService: ApiService;
  dialogService: DialogService;
  dialogRef: MatDialogRef<TestStatusDialogComponent>;
  dialog: MatDialog;
  showActionMenu: boolean;
  agenBasedConfig = [];
  dataParams = null;
  formatTestParams = null;
  previousAgentConfig = null;
  loadByTemplates = false;
  startTestURL = '/uicomm/StartTest';
  updateTestURL = '/uicomm/UpdateTest';
  createTestURL = '/uicomm/CreateTest';
  bulkTestURL = '/sbi/restconf/data/bulk/ietf-lmap-control:lmap';
  templateNameEvent: Subject<string>;
  selectedOriginator: Device;
  constructor(apiService: ApiService, dialogService: DialogService, dialog: MatDialog) {
    this.apiService = apiService;
    this.tabClass = BaseSetupTab;
    this.dialogService = dialogService;
    this.dialog = dialog;
    this.defaultId = 'setupService';
    this.id = null;
    this.defaultParamsById = {};
    this.options = {
      visible: false
    };
    this.setupActive = false;
    this.output = {};
    this.tabs = [];
    this.currentTab = null;
    this.isLoading = false;
    this.dirtyFlag = false;
    this.reOpenConfirmation = false;
    this.dataSaved = false;
    this.requestFailed = false;
    this.reOpen = () => {};
    this.templateNameEvent = new Subject<string>();
    this.dialogServiceMsgMap = {};
  }

  _getId(setupId: string): string {
    return `${this.defaultId}.${setupId}`;
  }

  //------------------------------------------------ Init config/data ----------------------------------------------------

  //eslint-disable-next-line
  _setTabs(setupConfig: setupConfig): any {
    //Fill fixed values
    merge(this, setupConfig.valuesFixed);
    this.method = this.currentConfig?.methodName || 'post';
    this.tabs = setupConfig.tabs.map(tab => {
      const tabsConfig = extend({}, tab, {
        id: [this.id, tab.id].join('.')
      });

      return new this.tabClass(this.apiService, tabsConfig, this);
    });
  }

  _initTabData(tab: BaseSetupTab, target: BaseSetupTab, defaultParamsById: outputParams): void {
    this._setDefaultParams(tab, target.output, defaultParamsById);
  }

  _setDefaultParams(tab: BaseSetupTab, targetOutput: outputParams, defaultParamsById = {}): void {
    for (const [key, item] of Object.entries(tab.output)) {
      if (isNumber(defaultParamsById[key]) || (!isEmpty(defaultParamsById[key]) && isEmpty(item))) {
        targetOutput[key] = cloneDeep(defaultParamsById[key]);
      }
    }
  }

  isValid(): boolean {
    const isValidStandardControls =
      (isEmpty(this.currentTab.form) || this.tabs?.every(tab => !tab?.form?.invalid)) &&
      !this.tabs.some(tab => {
        return tab.config.def.outputs.find(key => {
          return (
            this._getIndex(key, tab.config.def.outputOptional) === -1 &&
            this.getVolume(tab.output[key])
          );
        });
      });

    let isValidExtendedControls = true;
    !this.tabs.forEach(tab => {
      //eslint-disable-next-line
      forEach(tab.stateOfExtendedControls, (value, key) => {
        isValidExtendedControls = isValidExtendedControls && value.isValid;
      });
    });
    return isValidStandardControls && isValidExtendedControls;
  }

  _getIndex(item: string, array = []): number {
    return array.indexOf(item);
  }

  //------------------------------------------------ testsetup control ----------------------------------------------------

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    agentBasedConfig = []
  ): void {
    const dataParams = {
      setupId,
      setupConfig,
      defaultParams: defaultParamsById,
      onSuccess,
      onFail
    };
    // if (this._cancelOpenTestSetup(dataParams)) {
    //   return;
    // }
    this.agenBasedConfig = agentBasedConfig;
    this.dataParams = dataParams;
    this.onSuccess = onSuccess;
    this.onFail = onFail;
    this.reOpen = () => {
      this.id = null;
      this.openTestSetup(setupId, setupConfig, defaultParamsById, onSuccess, onFail);
    };
    this.reOpenConfirmation = false;
    this.mode = get(defaultParamsById, 'modeType', 'new');
    this.editMode = this.mode === 'edit' || get(defaultParamsById, 'editMode', false);
    const cloneMode = this.mode === 'clone' || get(defaultParamsById, 'cloneMode', false);
    this.currentConfig =
      !cloneMode && this.editMode ? setupConfig.updateConfig : setupConfig.createConfig;
    this.id = this._getId(setupId);

    // It is important to re-set data, possible left-over from previous run of testsetup
    this.output = {};
    this.formatData = this.currentConfig.formatData;
    this.formatTestParams = setupConfig.testParameters;
    this.dataUrl = this.currentConfig.dataUrl;
    this.defaultParamsById = defaultParamsById;
    this.dataSaved = false;
    this._setTabs(setupConfig);

    this.currentTab = firstL(this.tabs);
    if (!isEmpty(this.currentTab)) {
      this._initTabData(this.currentTab, this.currentTab, this.defaultParamsById);
    }
    this.setupActive = true;
    this.options.visible = true;
  }

  /* eslint-disable-next-line */
  _cancelOpenTestSetup(params: any): boolean {
    const id = this._getId(params.setupId);
    if (
      this.id !== id &&
      this.dirtyFlag &&
      this.setupActive &&
      !this.reOpenConfirmation &&
      this.dialogServiceMsgMap[this.id] !== undefined
    ) {
      this.askForConfirmation(this.reOpenTestSetupAfterConfirmation, params);
      return true;
    }

    return !!(
      this.id === id &&
      this.options.visible &&
      isEqual(params.setupConfig, this.dataParams.setupConfig) &&
      isEqual(params.defaultParams, this.defaultParamsById)
    );
  }

  toggleTabChange(index: number): void {
    this.currentTab.copyOutputs(this.output, this.currentTab.output);
    this.currentTab = this.tabs[index];
    this.currentTab.tabChange();
  }

  toggleClose(callBack = () => {}): void {
    if (this.dirtyFlag) {
      this.askForConfirmation(this.reOpenTestSetupAfterConfirmation, {
        close: true,
        callBack
      });
    } else {
      this.id = null;
      this.defaultParamsById = {};
      this.options.visible = false;
      this.formatData = null;
      this.method = null;
      unset(this, 'noResponseError');
      this.errors = [];
      this.reOpen = () => {};
      this.setupActive = false;
      this.dirtyFlag = false;
      this.editMode = false;
      this.previousAgentConfig = null;
      this.tabs = [];
      callBack();
    }
  }

  restoreAgentBasedConfig(agentType: string, testMode?: string, version?: string): void {
    const agentConfig = this.findAgentBasedConfig(agentType, testMode, version);
    const defaultConfig = this.dataParams.setupConfig;
    if (agentConfig && !isEqual(this.previousAgentConfig, agentConfig) && defaultConfig) {
      defaultConfig.tabs.forEach((tab, index) => {
        const currentTab = this.tabs[index];
        const tabConfig = agentConfig.tabsConfig.find(config => config.tabId === tab.id);
        let restoreOutputsValue = {};
        if (this.previousAgentConfig && tabConfig?.restoreOutputs) {
          restoreOutputsValue = tabConfig.restoreOutputs.reduce((obj, key) => {
            obj[key] = currentTab.output[key];
            return obj;
          }, {});
        }
        if (tabConfig) {
          tab.valuesDefault = Object.assign({}, tabConfig.valuesDefault, restoreOutputsValue);
          tab.options = tabConfig.options;
          tab.lookup = tabConfig.lookup;
          tab.def.outputs = tabConfig.outputs;
          tab.def.outputOptional = tabConfig.outputOptional;
        }
      });
      this.openTestSetup(
        this.dataParams.setupId,
        defaultConfig,
        this.defaultParamsById,
        null,
        null,
        this.agenBasedConfig
      );
      this.previousAgentConfig = Object.assign({}, agentConfig);
    }
  }

  isMtsAgentModel(agentType: string): boolean {
    const mtsAgentModel = [
      'TB/MTS-5800-100G',
      'TB/MTS-5800v2',
      'MTS-5882',
      'SC4800',
      'SC4800P',
      'TB-5800v2',
      'MTS-5800v2',
      'TB-5882',
      'TB-5800-100G',
      'MTS-5800-100G'
    ];
    return mtsAgentModel.some(item => item.toLowerCase() === agentType?.toLowerCase());
  }

  findAgentBasedConfig(agentType: string, testMode: string, version: string): any {
    agentType = this.isMtsAgentModel(agentType) ? AGENT_MODEL.MTS : agentType;
    return (
      this.agenBasedConfig.find(
        config => config.agentType === agentType + '-' + version + '-' + testMode
      ) ||
      this.agenBasedConfig.find(config => config.agentType === agentType + '-' + testMode) ||
      this.agenBasedConfig.find(config => config.agentType === agentType + '-' + version) ||
      this.agenBasedConfig.find(config => config.agentType === agentType) ||
      this.agenBasedConfig.find(config => config.agentType === 'default' + '-' + testMode) ||
      this.agenBasedConfig.find(config => config.agentType === 'default')
    );
  }

  openTestStatusDialog(message: string, action?: string): void {
    this.dialogRef = this.dialog.open(TestStatusDialogComponent, {
      width: '550px',
      disableClose: true,
      data: {
        isLoading: true,
        loaderMessage: message,
        message: null,
        isRequestFailed: null,
        errorDescription: null,
        icon: null,
        navigateToPage: null,
        action: action ? action : null
      }
    });
  }

  updateTestStatusDialog(message: string, action?: string): void {
    const data = {
      isLoading: this.isLoading,
      loaderMessage: null,
      message: message,
      isRequestFailed: this.requestFailed,
      errorDescription: this.errorDescription,
      icon: this.requestFailed ? 'mdi mdi-close' : 'mdi mdi-check',
      navigateToPage: !this.requestFailed,
      action: action ? action : null
    };
    if (this.dialogRef?.componentInstance) {
      this.dialogRef.componentInstance.updateData(data);
    }
  }

  updateBulkTestStatusDialog(
    message: string,
    errorDescription?: string,
    navigateToPage?: boolean,
    bulkErrors?: Array<{ name: string; issue: string }>
  ): void {
    const data = {
      isLoading: this.isLoading,
      loaderMessage: null,
      message: message,
      isRequestFailed: !navigateToPage,
      errorDescription: this.errorDescription || errorDescription,
      icon: !navigateToPage ? 'mdi mdi-close' : 'mdi mdi-check',
      navigateToPage: navigateToPage,
      bulkErrors: bulkErrors
    };
    if (this.dialogRef?.componentInstance) {
      this.dialogRef.componentInstance.updateData(data);
    } else {
      this.dialog.open(TestStatusDialogComponent, {
        width: '550px',
        disableClose: true,
        data: data
      });
    }
  }

  //eslint-disable-next-line
  getAgentLookUpData(lookUpFields: any, agent: Device): any {
    const lookUpData = {};
    let phyIndex = null;
    if (agent.deviceData.phyId && agent.deviceData?.capabilities?.['phy-list']) {
      phyIndex = agent.deviceData.phyId === 'p2' ? 1 : 0;
    }
    if (lookUpFields) {
      Object.keys(lookUpFields).forEach(key => {
        const values = lookUpFields?.[key];
        if (values && values.length) {
          values.forEach(capabilities => {
            capabilities = capabilities.replace('{index}', phyIndex);
            let capabilityValue = lookUpData[key] ?? get(agent.deviceData, capabilities);
            if (
              agent.deviceData?.capabilities?.system?.discoveryMode === 'IP-1' &&
              (key === 'srcAssignedIpAddress' || key === 'destAssignedIpAddress')
            ) {
              capabilityValue = this.getIpMinusOne(capabilityValue);
            }
            lookUpData[key] = capabilityValue;
          });
        }
      });
    }

    return lookUpData;
  }

  //This method will be invoked from dialog service after user confirmation
  /* eslint-disable-next-line */
  reOpenTestSetupAfterConfirmation(service: any, params: any): void {
    if (params !== null) {
      service.reOpenConfirmation = true;
      service.dirtyFlag = false;
      service.toggleClose();
      if (!params.close) {
        service.openTestSetup(
          params.setupId,
          params.setupConfig,
          params.defaultParams,
          params.onSuccess
        );
      }
      if (params.callBack) {
        params.callBack();
      }
    } else {
      service._saveSetupData();
    }
  }

  /* eslint-disable-next-line */
  askForConfirmation(onConfirm: any, fnParams: any): void {
    this.params = {
      id: this.editMode ? `${this.id}-edit` : this.id,
      callBackService: this,
      callBackFnParams: fnParams,
      callBackFuntion: onConfirm
    };
    if (this.dialogService) {
      this.dialogService.showDialog(this.id, 'body', this.params);
    }
  }

  onFinish(): void {
    this.onNext();
    return this._saveSetupData();
  }

  onNext(): void {
    //store tab output value to service output
    this.tabs.forEach(tab => {
      tab.copyOutputs(this.output, tab.output);
    });
  }

  /* eslint-disable-next-line */
  _saveSetupData(openOnFinish = false): any {
    //Save last Tab data
    this.dirtyFlag = false;
    const output = this._getFormatOutput();
    if (output.alias) {
      output.alias = output.alias.trim();
    }
    return this._saveRequest(output, openOnFinish);
  }

  /**
   * in case you want to simulate specific response from backend + delay,
   * you can try this:
   * - comment out original 'const request = ...apiService...'
   * - insert this:
   * import { of } from "rxjs";
   * import { delay } from "rxjs/internal/operators";
   * const request = of({result: true, success: true}).pipe(delay(10000));
   */

  /* eslint-disable-next-line */
  _saveRequest(output: any, openOnFinish?: boolean): any {
    this.isLoading = true;
    this.requestFailed = false;
    this.errorMessage = null;
    this.errorDescription = null;
    /* eslint-disable-next-line */
    const body = output;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const request = this.apiService[this.method.toLowerCase() + '$']<any>(this.dataUrl, body).pipe(
      first()
    );
    // we want to be able to notify the top level angular component about end of request.
    // if we return 'request' itself, then there is no guarantee which subscriber will run first (hence
    // there is a risk that your listener/angular component will be notified sooner than base.testsetup.service
    // updates inner state variables). Hence a separate Subject object.
    /* eslint-disable-next-line */
    const responseProcessed: Subject<any> = new Subject();
    request.subscribe(
      response => {
        this.dataSaved = true;
        this.isLoading = false;
        // notice the simplicity of emitted value! The reason: subscribers must use base.testsetup.service state variables
        // (such as dataSaved, requestFailed, isLoading, etc) to figure out what is the outcome of the call.
        responseProcessed.next(response);
        responseProcessed.complete();
      },
      error => {
        this.isLoading = false;
        this.requestFailed = true;
        this._handleErrorResponse(error, this.onFail);
        // see remark above on reasons for emitting a plain {} object.
        responseProcessed.next({});
        responseProcessed.complete();
      },
      () => {}
    );
    return responseProcessed.pipe(first());
  }
  /* eslint-disable-next-line */
  _downloadTestConfig(testConfig: any): any {
    let filename = 'TestConfig.json';
    if (
      testConfig.config &&
      testConfig.config.length &&
      testConfig.config[0].primaryConfig &&
      testConfig.config[0].primaryConfig['ietf-lmap-control:lmap'] &&
      testConfig.config[0].primaryConfig['ietf-lmap-control:lmap'].schedules &&
      testConfig.config[0].primaryConfig['ietf-lmap-control:lmap'].schedules.schedule.length
    ) {
      filename =
        testConfig.config[0].primaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].name +
        '.json';
    }

    // remove testConfig test data based on test type

    if (testConfig.test) {
      if (
        testConfig.test['extended-config'] &&
        (this.testType === 'truespeed' ||
          this.testType === 'udp' ||
          this.testType === 'ftp' ||
          this.testType === 'http')
      ) {
        delete testConfig.test['extended-config'];
        //MV-26413 - removed tpinfo section in download test config
        delete testConfig.test?.['test-list-data']?.['tpInfo'];
      } else {
        delete testConfig.test;
      }
    }
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' +
        encodeURIComponent(JSON.stringify({ 'fusion-controller:input': testConfig }))
    );

    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }
  /* eslint-disable-next-line */
  _saveControllerTest(testConfig: any): Observable<any> {
    const url = `${this.startTestURL}`;
    return this.apiService.post$<any>(url, testConfig);
  }

  /* eslint-disable-next-line */
  _saveBulkControllerTest(testConfig: any): Observable<any> {
    const url = `${this.bulkTestURL}`;
    return this.apiService.post$<any>(url, testConfig);
  }

  /* eslint-disable-next-line */
  _updateControllerTest(testConfig: any): Observable<any> {
    const url = `${this.updateTestURL}`;
    return this.apiService.post$<any>(url, testConfig);
  }

  /* eslint-disable-next-line */
  _createControllerTest(testConfig: any): Observable<any> {
    const url = `${this.createTestURL}`;
    return this.apiService.post$<any>(url, testConfig);
  }

  /**
   * The final step of testsetup/creating data in backend involves multiple asynchronous calls
   * to backend/any of them can fail.
   * This is a shared helper method to process failed calls.
   * @param {*} response Backend response
   * @param {*} onFail A special function to be invoked. An optional parameter. Example of use - dashboard will be notified
   * if testsetup fails to create new data - giving it a chance to pop up some message to end user.
   *
   */

  /* eslint-disable-next-line */
  _handleErrorResponse(response: any, onFail: any): void {
    this.isLoading = false;
    if (response && response.error && get(response, 'error.errors', false)) {
      if (get(response, 'error.errors', false) instanceof Array) {
        this.errorMessage = response.error.errors[0];
        this.errorDescription = response.error.errors[0];
      }
    } else if (response && response.error && get(response, 'error.message', false)) {
      this.errorMessage = response.error.message;
      this.errorDescription = response.error.message;
    } else if (
      response &&
      response.error &&
      (typeof response.error === 'string' || response.error instanceof String)
    ) {
      // analyze given string. if it contains html-formatted text, it is most likely to present a generic error page
      // from backend infrastructure. Default to 'unknown' error in this case/do not show returned HTML.
      if (!/.*<\/html>.*/.test(response.error)) {
        this.errorMessage = response.error;
        this.errorDescription = response.error;
      } else {
        if (response.status === 401) {
          this.errorMessage = 'Your login session has ended.';
          this.errorDescription = 'Your login session has ended.';
        } else if (response.status === 403) {
          this.errorMessage = 'You do not have permission for this action.';
          this.errorDescription = 'You do not have permission for this action.';
        } else {
          this.errorMessage = 'Reason unknown';
          this.errorDescription = 'Reason unknown';
        }
      }
    } else {
      if (response.status === 401) {
        this.errorMessage = 'Your login session has ended.';
        this.errorDescription = 'Your login session has ended.';
      } else if (response.status === 403) {
        this.errorMessage = 'You do not have permission for this action.';
        this.errorDescription = 'You do not have permission for this action.';
      } else {
        this.errorMessage = 'Reason unknown';
        this.errorDescription = 'Reason unknown';
      }
    }
    if (onFail) {
      onFail(response);
    }
  }

  //----------------------------------------------- Format output data ---------------------------------------------------

  /* eslint-disable-next-line */
  _getFormatOutput(output = this.output, format = this.formatData): any {
    const formattedOutput = {};
    forEach(format, (param, key) => {
      if (!isEmpty(param)) {
        formattedOutput[key] = this._getFormatOutputItem(output[key], param);
      } else {
        formattedOutput[key] = output[key];
      }
    });
    return formattedOutput;
  }

  /* eslint-disable-next-line */
  _getFormatOutputItem(output: any, format: any): any {
    let newOutput = {};
    if (format.type === 'array') {
      newOutput = output.map(function (item) {
        let newItem = {};

        if (format.param) {
          newItem = item[format.param];
        } else {
          format.params.forEach(function (key) {
            newItem[key] = item[key];
          });
        }
        return newItem;
      });
    } else if (format.type === 'object') {
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      format.params.forEach(function (item: any) {
        //TODO fix it
        if (typeof item === 'object') {
          const values = output[item.value];
          values.forEach(value => unset(value, 'selected'));
          newOutput[item.key] = values;
        } else {
          newOutput[item] = output[item];
        }
      });
    } else if (format.type === 'key:value') {
      newOutput = output.map(item => {
        return `${item.key}:${item.value}`;
      });
    } else if (format.param) {
      newOutput = output[format.param];
    } else {
      newOutput = format;
    }
    return newOutput;
  }

  //---------------------------------------GetTestParameters-------------------------

  _getConfigParameters(output = this.output): any {
    const formattedParams = {};
    if (this.formatTestParams) {
      forEach(this.formatTestParams, (param, key) => {
        if (!isNil(output[key])) {
          formattedParams[key] = output[key];
        } else if (!isNil(param)) {
          formattedParams[key] = param;
        }
      });
      return formattedParams;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  removeNullValues(props: any): any {
    const target = {};
    if (props) {
      Object.entries(props).forEach(([k, v]) => !isNil(v) && (target[k] = v));
    }
    return target;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  _createTemplateFormat(
    configParams: object,
    selectedOriginator: Device,
    selectedDestination: Device,
    selectedApplication = null
  ): any {
    const format = {};
    format['test-parameters'] = configParams;
    format['agent'] = {};
    if (Array.isArray(selectedOriginator)) {
      format['agent'] = { primary: [], secondary: {} };
      for (let i = 0; i < selectedOriginator.length; i++) {
        format['agent']['primary'].push({
          //QT device will have multiple port, so we are sending devicename to map it properly
          id: selectedOriginator[i].getDeviceName()
        });
      }
    } else if (selectedOriginator) {
      format['agent']['primary'] = {
        //QT device will have multiple port, so we are sending devicename to map it properly
        id: selectedOriginator.getDeviceName()
      };
    }
    if (Array.isArray(selectedDestination)) {
      format['agent']['secondary'] = [];
      for (let i = 0; i < selectedDestination.length; i++) {
        format['agent']['secondary'].push({
          //QT device will have multiple port, so we are sending devicename to map it properly
          id: selectedDestination[i].getDeviceName()
        });
      }
    } else if (selectedDestination) {
      format['agent']['secondary'] = {
        id: selectedDestination.getDeviceName()
      };
    }
    if (selectedApplication) {
      format['agent']['secondary'] = {
        id: selectedApplication.key
      };
    }
    format['test-data'] = {
      mode: this.output.testMode,
      customer: {
        name: this.output.customer,
        contact: this.output.contact,
        email: this.output.email,
        phone: this.output.phone
      },
      technician: {
        name: this.output.technician,
        id: this.output.technicianId,
      },
      testInfo:{
        comments: this.output.comments
      }
    };
    return format;
  }

  //eslint-disable-next-line
  getEngineInfo(deviceData: IDeviceData): IEngineInfo {
    let phyInfo;
    if (deviceData?.capabilities?.phy) {
      phyInfo = deviceData?.capabilities?.phy;
    } else if (deviceData?.capabilities?.['phy-list']) {
      phyInfo = deviceData?.capabilities?.['phy-list'].find(
        item => item['phy-id'] === deviceData.phyId
      );
    }
    const port =
      deviceData?.connections[0]?.uri.split(':')[2] === '0' && deviceData.comMode === 'firewall'
        ? 443
        : deviceData?.connections[0]?.uri.split(':')[2];
    return {
      'agent-type': deviceData?.capabilities?.type,
      'engine-type': 'vprobe',
      address: deviceData?.connections
        ? deviceData?.connections[0]?.uri.split(':')[1].replace('//', '')
        : null,
      port: port,
      'test-ip': phyInfo['default-test-ip'] ? phyInfo['default-test-ip'] : null,
      'test-subnet-mask': phyInfo['default-test-mask'] ? phyInfo['default-test-mask'] : null,
      'test-gateway': phyInfo['default-test-gateway'] ? phyInfo['default-test-gateway'] : null
    };
  }

  //-------------------------------------------------Helpers------------------------------------------------------------
  getRange(num1: number, num2: number): number[] {
    const start = Math.min(num1, num2);
    const end = Math.max(num1, num2);

    // end + 1 to include the 'end' number
    return range(start, end + 1);
  }

  getIpMinusOne(ip: string): string | null {
    const parts = ip.split('.').map(Number);
    if (parts.length !== 4 || parts.some(p => isNaN(p) || p < 0 || p > 255)) {
      return null; // invalid IP
    }

    // Convert to 32-bit integer
    let ipInt = (parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3];

    if (ipInt === 0) {
      return null; // can't decrement 0.0.0.0
    }

    // Decrement
    ipInt = ipInt - 1;

    // Convert back to dotted decimal
    const newParts = [
      (ipInt >>> 24) & 0xff,
      (ipInt >>> 16) & 0xff,
      (ipInt >>> 8) & 0xff,
      ipInt & 0xff
    ];

    return newParts.join('.');
  }

  private getVolume(item): boolean {
    if (isNumber(item) || typeof item === 'boolean') {
      return false;
    } else return isEmpty(item);
  }
}
